import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import FacturacionConfig from './FacturacionConfig';
import SyncErrorsPanel from './SyncErrorsPanel';
import SyncScheduleConfig from './SyncScheduleConfig';
import DataConnections from './DataConnections';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, DollarSign, CreditCard, Clock, Repeat, Zap, AlertTriangle, Building2,
  Filter, ChevronDown, ChevronUp, Search, RefreshCw, Settings, Info,
  ArrowUpRight, ArrowDownRight, ChevronLeft, ChevronRight, X, Eye,
  Download, FileDown, History, AlertCircle, Calendar, Database
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend, ComposedChart, Line, Area, AreaChart
} from 'recharts';

const formatCurrency = (value) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(value);

const formatCurrencyCompact = (value) => {
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const STATUS_COLORS = {
  cobrada: 'bg-emerald-100 text-emerald-800',
  pendiente: 'bg-amber-100 text-amber-800',
  vencida: 'bg-red-100 text-red-800',
  parcial: 'bg-blue-100 text-blue-800',
  cancelada: 'bg-gray-100 text-gray-600',
  pendiente_facturar: 'bg-purple-100 text-purple-800',
};

const STATUS_LABELS = {
  cobrada: 'Cobrada', pendiente: 'Pendiente', vencida: 'Vencida',
  parcial: 'Parcial', cancelada: 'Cancelada', pendiente_facturar: 'Pend. Facturar',
};

const TYPE_LABELS = { iguala: 'Iguala', one_shot: 'One Shot' };

const CHART_COLORS = [
  '#e11d48', '#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899',
  '#06b6d4', '#f97316', '#14b8a6', '#a855f7', '#6366f1', '#84cc16'
];

// ============ KPI Card ============
function KPICard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card className="relative overflow-hidden" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-lg sm:text-xl font-bold tabular-nums truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {value}
            </p>
            {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color || 'bg-primary/10'}`}>
            <Icon className={`w-4 h-4 ${color ? 'text-white' : 'text-primary'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Filters Panel ============
function FiltersPanel({ filters, setFilters, options, onClear }) {
  const [open, setOpen] = useState(false);
  const activeCount = Object.values(filters).filter(v => v && v !== 'all').length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2 flex-wrap">
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="toggle-filters-btn">
            <Filter className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Filtros</span>
            {activeCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-[10px] flex items-center justify-center rounded-full">
                {activeCount}
              </Badge>
            )}
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </Button>
        </CollapsibleTrigger>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} className="text-xs text-muted-foreground gap-1" data-testid="clear-filters-btn">
            <X className="w-3 h-3" /> Limpiar
          </Button>
        )}
      </div>
      <CollapsibleContent className="mt-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <FilterSelect label="Año" value={filters.year} onChange={v => setFilters(f => ({...f, year: v}))}
            options={options.years?.map(y => ({value: y, label: y})) || []} testId="filter-year" />
          <FilterSelect label="Mes" value={filters.month} onChange={v => setFilters(f => ({...f, month: v}))}
            options={[...Array(12)].map((_, i) => ({value: String(i+1), label: ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][i]}))} testId="filter-month" />
          <FilterSelect label="Status" value={filters.status} onChange={v => setFilters(f => ({...f, status: v}))}
            options={options.statuses?.map(s => ({value: s.value, label: s.label})) || []} testId="filter-status" />
          <FilterSelect label="Empresa" value={filters.company} onChange={v => setFilters(f => ({...f, company: v}))}
            options={options.companies?.map(c => ({value: c, label: c})) || []} testId="filter-company" />
          <FilterSelect label="Cliente" value={filters.client} onChange={v => setFilters(f => ({...f, client: v}))}
            options={options.clients?.map(c => ({value: c, label: c})) || []} testId="filter-client" />
          <FilterSelect label="Vendedor" value={filters.seller} onChange={v => setFilters(f => ({...f, seller: v}))}
            options={options.sellers?.map(s => ({value: s, label: s})) || []} testId="filter-seller" />
          <FilterSelect label="Encargado" value={filters.responsible} onChange={v => setFilters(f => ({...f, responsible: v}))}
            options={options.responsibles?.map(r => ({value: r, label: r})) || []} testId="filter-responsible" />
          <FilterSelect label="Tipo" value={filters.invoice_type} onChange={v => setFilters(f => ({...f, invoice_type: v}))}
            options={options.types?.map(t => ({value: t.value, label: t.label})) || []} testId="filter-type" />
          <FilterSelect label="Moneda" value={filters.currency} onChange={v => setFilters(f => ({...f, currency: v}))}
            options={options.currencies?.map(c => ({value: c, label: c})) || []} testId="filter-currency" />
          <FilterSelect label="Concepto" value={filters.concept_key} onChange={v => setFilters(f => ({...f, concept_key: v}))}
            options={options.concepts?.map(c => ({value: c, label: c})) || []} testId="filter-concept" />
          <div className="col-span-2">
            <p className="text-[10px] text-muted-foreground mb-1">No. Factura</p>
            <Input placeholder="Buscar..." value={filters.invoice_number || ''} className="h-8 text-xs"
              onChange={e => setFilters(f => ({...f, invoice_number: e.target.value || undefined}))}
              data-testid="filter-invoice-number" />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground mb-1">{label}</p>
      <Select value={value || 'all'} onValueChange={v => onChange(v === 'all' ? undefined : v)}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value="all">Todos</SelectItem>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ============ Custom Tooltip ============
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ============ Invoice Detail Dialog ============
function InvoiceDetailDialog({ invoice, open, onClose }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invoice?.id) {
      setLoading(true);
      api.get(`/facturacion/invoices/${invoice.id}`)
        .then(r => setLines(r.data.lines || []))
        .catch(() => toast.error('Error al cargar detalle'))
        .finally(() => setLoading(false));
    }
  }, [open, invoice?.id]);

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Factura {invoice.invoice_number}
          </DialogTitle>
          <DialogDescription>Detalle de conceptos y líneas</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div><span className="text-muted-foreground">Cliente:</span> <strong>{invoice.client_name}</strong></div>
          <div><span className="text-muted-foreground">Empresa:</span> <strong>{invoice.company_name}</strong></div>
          <div><span className="text-muted-foreground">Fecha:</span> <strong>{invoice.invoice_date}</strong></div>
          <div><span className="text-muted-foreground">Vendedor:</span> <strong>{invoice.seller_name}</strong></div>
          <div><span className="text-muted-foreground">Status:</span> <Badge className={STATUS_COLORS[invoice.invoice_status]}>{STATUS_LABELS[invoice.invoice_status]}</Badge></div>
          <div><span className="text-muted-foreground">Tipo:</span> <strong>{TYPE_LABELS[invoice.invoice_type]}</strong></div>
          <div><span className="text-muted-foreground">Total:</span> <strong className="text-primary">{formatCurrency(invoice.total_amount)}</strong></div>
          <div><span className="text-muted-foreground">Cobrado:</span> <strong className="text-emerald-600">{formatCurrency(invoice.collected_amount)}</strong></div>
        </div>
        <Separator />
        <div className="mt-3">
          <h4 className="font-semibold text-sm mb-2">Conceptos ({lines.length})</h4>
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={line.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{line.concept}</p>
                    <p className="text-muted-foreground truncate">{line.description}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="font-semibold">{formatCurrency(line.total_amount)}</p>
                    <p className="text-muted-foreground">{line.quantity} x {formatCurrency(line.unit_price)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN COMPONENT ============
export default function Facturacion() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, config, errors, schedule, connections
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [syncStatus, setSyncStatus] = useState({});
  const [invoices, setInvoices] = useState({ invoices: [], total: 0, page: 1, total_pages: 1 });
  const [dimensionData, setDimensionData] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [deletingDemo, setDeletingDemo] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Handle URL params for view routing (e.g., from OAuth callback)
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'connections') {
      setActiveView('connections');
    }
  }, [searchParams]);

  const [filters, setFilters] = useState({});

  const filterParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') p[k] = v; });
    return p;
  }, [filters]);

  // Load filter options once
  useEffect(() => {
    api.get('/facturacion/filter-options').then(r => setFilterOptions(r.data)).catch(() => {});
    api.get('/facturacion/sync-status').then(r => setSyncStatus(r.data)).catch(() => {});
  }, []);

  // Load dashboard data when filters change
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, monthRes, companyRes, clientRes, sellerRes, typeRes] = await Promise.all([
        api.get('/facturacion/dashboard', { params: filterParams }),
        api.get('/facturacion/charts/monthly', { params: filterParams }),
        api.get('/facturacion/charts/by-dimension', { params: { ...filterParams, dimension: 'company' } }),
        api.get('/facturacion/charts/by-dimension', { params: { ...filterParams, dimension: 'client' } }),
        api.get('/facturacion/charts/by-dimension', { params: { ...filterParams, dimension: 'seller' } }),
        api.get('/facturacion/charts/by-dimension', { params: { ...filterParams, dimension: 'type' } }),
      ]);
      setDashboardData(dashRes.data);
      setMonthlyData(monthRes.data);
      setDimensionData({ company: companyRes.data, client: clientRes.data, seller: sellerRes.data, type: typeRes.data });
    } catch {
      toast.error('Error al cargar datos de facturación');
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load invoices table
  const loadInvoices = useCallback(async () => {
    try {
      const res = await api.get('/facturacion/invoices', {
        params: { ...filterParams, page, page_size: 15, search: searchTerm || undefined }
      });
      setInvoices(res.data);
    } catch { /* silent */ }
  }, [filterParams, page, searchTerm]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // El auto-seed de datos demo fue retirado: el dashboard sólo muestra datos reales.

  const deleteDemoData = async () => {
    if (!window.confirm('Se eliminarán las facturas demo (las que no provienen de una sincronización). Los datos sincronizados desde Google Sheets no se tocan. ¿Continuar?')) return;
    setDeletingDemo(true);
    try {
      const res = await api.delete('/facturacion/demo-data');
      toast.success(res.data.message);
      await Promise.all([loadData(), loadInvoices()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron eliminar los datos demo');
    } finally {
      setDeletingDemo(false);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setPage(1);
  };

  const exportFile = async (type, exportType = 'invoices') => {
    setExporting(true);
    try {
      const params = { ...filterParams };
      if (type === 'excel') params.export_type = exportType;
      const res = await api.get(`/facturacion/export/${type}`, {
        params, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      link.download = `facturacion_${exportType}_${new Date().toISOString().slice(0,10)}.${ext}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Archivo ${ext.toUpperCase()} descargado`);
    } catch {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      const res = await api.get('/facturacion/sync/history', { params: { limit: 10 } });
      setSyncHistory(res.data);
    } catch { /* silent */ }
  };

  const kpis = dashboardData?.kpis || {};
  const isAdmin = user?.role && ['super_admin', 'admin', 'admin_cliente'].includes(user.role);

  // Show config page
  if (activeView === 'config') {
    return <FacturacionConfig onBack={() => setActiveView('dashboard')} />;
  }

  // Show sync errors panel
  if (activeView === 'errors') {
    return <SyncErrorsPanel onBack={() => setActiveView('dashboard')} />;
  }

  // Show schedule config
  if (activeView === 'schedule') {
    return (
      <SyncScheduleConfig 
        onBack={() => setActiveView('dashboard')} 
        onViewErrors={() => setActiveView('errors')}
      />
    );
  }

  // Show data connections hub
  if (activeView === 'connections') {
    return <DataConnections onBack={() => setActiveView('dashboard')} />;
  }

  if (loading && !dashboardData) {
    return (
      <div className="p-4 sm:p-6 space-y-6" data-testid="facturacion-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="facturacion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Facturación
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Dashboard ejecutivo de facturación y cobranza
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {dashboardData?.is_demo && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 gap-1" data-testid="demo-data-badge">
              <Info className="w-3 h-3" /> {dashboardData.demo_invoices} facturas demo
            </Badge>
          )}
          {!syncStatus.connected && (
            <Badge variant="outline" className="text-gray-500 border-gray-300 gap-1">
              <Settings className="w-3 h-3" /> Google Sheets no conectado
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={loadData} className="gap-1.5" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
          <div className="relative group">
            <Button variant="outline" size="sm" disabled={exporting} className="gap-1.5" data-testid="export-btn">
              <Download className={`w-3.5 h-3.5 ${exporting ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">Exportar</span>
              <ChevronDown className="w-3 h-3" />
            </Button>
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[180px] hidden group-hover:block">
              <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                onClick={() => exportFile('excel', 'invoices')} data-testid="export-excel-invoices">
                <FileDown className="w-3.5 h-3.5" /> Excel (Facturas)
              </button>
              <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                onClick={() => exportFile('excel', 'lines')} data-testid="export-excel-lines">
                <FileDown className="w-3.5 h-3.5" /> Excel (Conceptos)
              </button>
              <div className="border-t my-1" />
              <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                onClick={() => exportFile('pdf')} data-testid="export-pdf">
                <FileText className="w-3.5 h-3.5" /> PDF Ejecutivo
              </button>
            </div>
          </div>
          {isAdmin && (
            <div className="relative group">
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="admin-menu-btn">
                <Settings className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Admin</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px] hidden group-hover:block">
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                  onClick={() => setActiveView('connections')} data-testid="nav-connections">
                  <Database className="w-3.5 h-3.5" /> Conexión de Datos
                </button>
                <div className="border-t my-1" />
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                  onClick={() => setActiveView('config')} data-testid="nav-config">
                  <Settings className="w-3.5 h-3.5" /> Configurar Google
                </button>
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                  onClick={() => setActiveView('schedule')} data-testid="nav-schedule">
                  <Calendar className="w-3.5 h-3.5" /> Sincronización
                </button>
                <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                  onClick={() => setActiveView('errors')} data-testid="nav-errors">
                  <AlertCircle className="w-3.5 h-3.5" /> Errores de Sync
                </button>
                {dashboardData?.is_demo && (
                  <>
                    <div className="border-t my-1" />
                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-600 flex items-center gap-2 disabled:opacity-50"
                      onClick={deleteDemoData} disabled={deletingDemo} data-testid="delete-demo-data-btn">
                      <AlertTriangle className="w-3.5 h-3.5" /> {deletingDemo ? 'Eliminando...' : 'Eliminar datos demo'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <FiltersPanel filters={filters} setFilters={setFilters} options={filterOptions} onClear={clearFilters} />

      {dashboardData?.total_invoices === 0 && (
        <Card data-testid="no-data-empty-state">
          <CardContent className="p-6 text-center space-y-2">
            <Database className="w-8 h-8 mx-auto text-muted-foreground" />
            <h3 className="text-sm font-semibold">Aún no hay facturas sincronizadas</h3>
            <p className="text-xs text-muted-foreground">
              Conecta tu Google Sheet y ejecuta una sincronización para ver aquí tus datos reales.
            </p>
            {isAdmin && (
              <Button size="sm" className="gap-1.5 mt-1" onClick={() => setActiveView('connections')} data-testid="empty-state-connect-btn">
                <Database className="w-3.5 h-3.5" /> Conexión de Datos
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {(dashboardData?.invalid_amounts > 0 || dashboardData?.invoices_without_date > 0) && (
        <Card className="border-amber-200 bg-amber-50" data-testid="data-quality-alert">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 space-y-0.5">
              {dashboardData.invalid_amounts > 0 && (
                <p data-testid="alert-invalid-amounts">
                  <strong>{dashboardData.invalid_amounts}</strong> registro(s) con importe ilegible en la hoja (no se contabilizan como $0). Revísalos en Admin → Errores de Sync.
                </p>
              )}
              {dashboardData.invoices_without_date > 0 && (
                <p data-testid="alert-missing-dates">
                  <strong>{dashboardData.invoices_without_date}</strong> factura(s) sin fecha válida quedan fuera del filtro por año.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KPICard title="Total Facturado" value={formatCurrency(kpis.total_facturado || 0)} icon={FileText} color="bg-primary" />
        <KPICard title="Total Cobrado" value={formatCurrency(kpis.total_cobrado || 0)} icon={DollarSign} color="bg-emerald-500" />
        <KPICard title="Por Cobrar" value={formatCurrency(kpis.total_por_cobrar || 0)} icon={Clock} color="bg-amber-500" />
        <KPICard title="Pend. Facturar" value={formatCurrency(kpis.pendiente_facturar || 0)} icon={AlertTriangle} color="bg-purple-500"
          subtitle={dashboardData?.pendiente_facturar_count ? `${dashboardData.pendiente_facturar_count} registros` : undefined} />
        <KPICard title="Igualas" value={formatCurrency(kpis.total_igualas || 0)} icon={Repeat} subtitle="Facturación recurrente" />
        <KPICard title="One Shots" value={formatCurrency(kpis.total_oneshots || 0)} icon={Zap} subtitle="Proyectos únicos" />
        <KPICard title="Ejercicios Ant." value={formatCurrency(kpis.pendiente_ejercicios_anteriores || 0)} icon={AlertTriangle} subtitle="Pendientes de años anteriores" />
        {dashboardData?.by_company?.length > 0 && (
          <KPICard title={dashboardData.by_company[0].company}
            value={formatCurrency(dashboardData.by_company[0].total)} icon={Building2} />
        )}
      </div>

      {/* Charts Row 1: Monthly + Cobrado vs Por Cobrar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Facturación Mensual</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCurrencyCompact} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="facturado" fill="#e11d48" name="Facturado" radius={[4, 4, 0, 0]} />
                <Line dataKey="cobrado" stroke="#10b981" name="Cobrado" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Cobrado vs Por Cobrar</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCurrencyCompact} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="cobrado" fill="#10b981" name="Cobrado" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="por_cobrar" fill="#f59e0b" name="Por Cobrar" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: By Company + By Client */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DimensionChart title="Facturación por Empresa" data={dimensionData.company} />
        <DimensionChart title="Top Clientes" data={dimensionData.client} />
      </div>

      {/* Charts Row 3: By Seller + Igualas vs One Shots */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DimensionChart title="Facturación por Vendedor" data={dimensionData.seller} />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Igualas vs One Shots</CardTitle>
          </CardHeader>
          <CardContent className="h-64 flex items-center justify-center">
            {dimensionData.type?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie data={dimensionData.type.map(d => ({...d, name: TYPE_LABELS[d.name] || d.name}))}
                    dataKey="total" nameKey="name" cx="50%" cy="50%"
                    outerRadius={80} innerRadius={40} paddingAngle={4} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {dimensionData.type.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">Sin datos</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-sm font-semibold">
              Detalle de Facturas ({invoices.total})
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Buscar factura..." className="h-8 text-xs pl-8 w-48"
                  value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                  data-testid="invoice-search" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="invoices-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2 font-medium">Factura</th>
                <th className="text-left p-2 font-medium hidden sm:table-cell">Empresa</th>
                <th className="text-left p-2 font-medium">Cliente</th>
                <th className="text-left p-2 font-medium hidden md:table-cell">Fecha</th>
                <th className="text-left p-2 font-medium hidden lg:table-cell">Vendedor</th>
                <th className="text-right p-2 font-medium">Total</th>
                <th className="text-right p-2 font-medium hidden sm:table-cell">Cobrado</th>
                <th className="text-right p-2 font-medium hidden md:table-cell">Pendiente</th>
                <th className="text-center p-2 font-medium">Status</th>
                <th className="text-center p-2 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.invoices.map(inv => (
                <tr key={inv.id} className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => { setSelectedInvoice(inv); setDetailOpen(true); }}>
                  <td className="p-2 font-medium">{inv.invoice_number}</td>
                  <td className="p-2 hidden sm:table-cell text-muted-foreground">{inv.company_name}</td>
                  <td className="p-2 max-w-[120px] truncate">{inv.client_name}</td>
                  <td className="p-2 hidden md:table-cell text-muted-foreground">{inv.invoice_date}</td>
                  <td className="p-2 hidden lg:table-cell text-muted-foreground">{inv.seller_name}</td>
                  <td className="p-2 text-right font-medium tabular-nums">{formatCurrency(inv.total_amount)}</td>
                  <td className="p-2 text-right text-emerald-600 tabular-nums hidden sm:table-cell">{formatCurrency(inv.collected_amount)}</td>
                  <td className="p-2 text-right text-amber-600 tabular-nums hidden md:table-cell">{formatCurrency(inv.pending_amount)}</td>
                  <td className="p-2 text-center">
                    <Badge className={`text-[10px] ${STATUS_COLORS[inv.invoice_status]}`}>
                      {STATUS_LABELS[inv.invoice_status]}
                    </Badge>
                  </td>
                  <td className="p-2 text-center">
                    <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                  </td>
                </tr>
              ))}
              {invoices.invoices.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No se encontraron facturas</td></tr>
              )}
            </tbody>
          </table>
          {/* Pagination */}
          {invoices.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t">
              <p className="text-xs text-muted-foreground">
                Página {invoices.page} de {invoices.total_pages}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page-btn">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= invoices.total_pages}
                  onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <InvoiceDetailDialog invoice={selectedInvoice} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </div>
  );
}

// ============ Dimension Bar Chart ============
function DimensionChart({ title, data }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="h-64">
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatCurrencyCompact} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="total" fill="#e11d48" name="Total" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Sin datos</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
